import { createSlice } from '@reduxjs/toolkit';
import {
  createExpense,
  fetchAllExpenses,
  fetchExpenseById,
  updateExpense,
  deleteExpense,
  fetchExpensesByEmployeeId,
  fetchUniquePaymentMethods,
  fetchExpensesBySupervisorHierarchy
} from './expenseActions';
import { ExpenseState } from './expenseTypes';

const initialState: ExpenseState = {
  records: [],
  expenseByEmployee: [],
  expensesBySupervisorHierarchy: [],
  uniquePaymentMethods:[],
  selectedRecord: undefined,
  loadingStates: {
    create: false,
    fetchAll: false,
    fetchById: false,
    fetchByEmployeeId: false,
    fetchBySupervisorHierarchy: false,
    fetchPaymentMethods: false,
    update: false,
    delete: false
  },
  errors: {}
};

const expenseSlice = createSlice({
  name: 'expense',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createExpense.pending, (state) => {
        state.loadingStates.create = true;
        state.errors.create = undefined;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.loadingStates.create = false;
        state.records.push(action.payload.data);
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.loadingStates.create = false;
        state.errors.create = action.payload as string;
      })

      // Get All
      .addCase(fetchAllExpenses.pending, (state) => {
        state.loadingStates.fetchAll = true;
        state.errors.fetchAll = undefined;
      })
      .addCase(fetchAllExpenses.fulfilled, (state, action) => {
        state.loadingStates.fetchAll = false;
        state.records = action.payload.data;
      })
      .addCase(fetchAllExpenses.rejected, (state, action) => {
        state.loadingStates.fetchAll = false;
        state.errors.fetchAll = action.payload as string;
      })

      // Get By ID
      .addCase(fetchExpenseById.pending, (state) => {
        state.loadingStates.fetchById = true;
        state.errors.fetchById = undefined;
      })
      .addCase(fetchExpenseById.fulfilled, (state, action) => {
        state.loadingStates.fetchById = false;
        state.selectedRecord = action.payload.data;
      })
      .addCase(fetchExpenseById.rejected, (state, action) => {
        state.loadingStates.fetchById = false;
        state.errors.fetchById = action.payload as string;
      })

      // Update
      .addCase(updateExpense.pending, (state) => {
        state.loadingStates.update = true;
        state.errors.update = undefined;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.loadingStates.update = false;
        const index = state.records.findIndex(e => e.expense_id === action.payload.id);
        if (index !== -1) {
          state.records[index] = { ...action.payload.data, expense_id: action.payload.id };
        }
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.loadingStates.update = false;
        state.errors.update = action.payload as string;
      })
      .addCase(fetchExpensesByEmployeeId.pending, (state) => {
        state.loadingStates.fetchByEmployeeId = true;
        state.errors.fetchByEmployeeId = undefined;
      })
      .addCase(fetchExpensesByEmployeeId.fulfilled, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.expenseByEmployee = action.payload.data;
      })
      .addCase(fetchExpensesByEmployeeId.rejected, (state, action) => {
        state.loadingStates.fetchByEmployeeId = false;
        state.errors.fetchByEmployeeId = action.payload as string;
      })

      .addCase(fetchUniquePaymentMethods.pending, (state) => {
        state.loadingStates.fetchPaymentMethods = true;
        state.errors.fetchPaymentMethods = undefined;
      })
      .addCase(fetchUniquePaymentMethods.fulfilled, (state, action) => {
        state.loadingStates.fetchPaymentMethods = false;
        state.uniquePaymentMethods = action.payload.data;
      })
      .addCase(fetchUniquePaymentMethods.rejected, (state, action) => {
        state.loadingStates.fetchPaymentMethods = false;
        state.errors.fetchPaymentMethods = action.payload as string;
      })

      // Fetch By Supervisor Hierarchy
      .addCase(fetchExpensesBySupervisorHierarchy.pending, (state) => {
        state.loadingStates.fetchBySupervisorHierarchy = true;
        state.errors.fetchBySupervisorHierarchy = undefined;
      })
      .addCase(fetchExpensesBySupervisorHierarchy.fulfilled, (state, action) => {
        state.loadingStates.fetchBySupervisorHierarchy = false;
        state.expensesBySupervisorHierarchy = action.payload.data;
      })
      .addCase(fetchExpensesBySupervisorHierarchy.rejected, (state, action) => {
        state.loadingStates.fetchBySupervisorHierarchy = false;
        state.errors.fetchBySupervisorHierarchy = action.payload as string;
      })

      // Delete
      .addCase(deleteExpense.pending, (state) => {
        state.loadingStates.delete = true;
        state.errors.delete = undefined;
      })
    .addCase(deleteExpense.fulfilled, (state, action) => {
      state.loadingStates.delete = false;
      state.records = state.records.filter(e => e.expense_id !== action.payload);
    })
    .addCase(deleteExpense.rejected, (state, action) => {
      state.loadingStates.delete = false;
      state.errors.delete = action.payload as string;
    });
}
});

export default expenseSlice.reducer;
